import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";

const SepticSystemReplacementThunderBayPage = () => {
  return (
    <div>
      <SEO
        title="Septic System Replacement Thunder Bay | Groundhogs™ Septic Systems"
        description="Septic system replacement in Thunder Bay and Northwestern Ontario (Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, Murillo). Engineered designs, permits, tank + field replacement, and Eljen options."
        keywords="septic system replacement Thunder Bay, septic tank and field replacement, septic field replacement Thunder Bay, septic replacement near me, Eljen system replacement"
        canonicalPath="/services/septic-system-replacement-thunder-bay"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: "Septic System Replacement in Thunder Bay & Northwestern Ontario",
              description:
                "Professional septic system replacement (tank and field) and engineered septic design for Thunder Bay and surrounding Northwestern Ontario communities.",
              areaServed: {
                "@type": "AdministrativeArea",
                name: "Thunder Bay & Northwestern Ontario",
              },
              provider: {
                "@type": "LocalBusiness",
                name: "Groundhogs Septic Systems",
                url: "https://groundhogsseptics.com/",
              },
              serviceType: [
                "Septic system replacement",
                "Septic tank replacement",
                "Septic field replacement",
                "Engineered septic system design",
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How much does septic system replacement cost in Thunder Bay?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Costs vary by soil (clay vs sand), bedrock depth, groundwater, access, and system selection (traditional vs Eljen/raised systems). After a site assessment we provide a clear replacement plan and quote.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can you replace just the septic tank and keep the existing field?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Sometimes. If the field is still performing and compliant, a tank-only replacement may be possible. We evaluate the field and explain options before work begins.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Do you handle permits and engineered designs?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. We coordinate required permits/inspections and work with designers/engineers to deliver Ontario Building Code-compliant systems.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What replacement options do you install?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Depending on your site, we install conventional systems, raised beds/mounds, and advanced Eljen GSF systems for clay, bedrock, high groundwater, or tight lots.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Which areas do you serve near Thunder Bay?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "We serve Thunder Bay and surrounding communities including Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, and Murillo.",
                  },
                },
              ],
            },
          ],
        }}
      />
      <Header />

      <main className="pt-32 pb-16" style={{ backgroundColor: "var(--white)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm mb-4" style={{ color: "var(--text-light)" }}>
            Specialist septic system design, installation and replacement for Thunder Bay and Northwestern Ontario. We do
            not offer septic tank pumping or cleaning services.
          </p>
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 text-gray-500">
              <li>
                <Link to="/" className="hover:underline text-orange-600">
                  Home
                </Link>
              </li>
              <li className="mx-1">/</li>
              <li>
                <Link to="/services" className="hover:underline text-orange-600">
                  Services
                </Link>
              </li>
              <li className="mx-1">/</li>
              <li className="text-gray-700">Septic System Replacement Thunder Bay</li>
            </ol>
          </nav>

          {/* Hero / Intro */}
          <section className="gh-article-heading mb-10" data-reveal>
            <h1
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic System Installation &amp; Replacement in Thunder Bay &amp; Northwestern Ontario
            </h1>
            <p className="text-lg mb-4" style={{ color: "var(--text-light)" }}>
              Building new, adding a cottage, or dealing with a failing septic system in Thunder Bay? Between clay soil,
              shallow bedrock, high groundwater and harsh freeze–thaw cycles, most properties need a properly engineered
              septic system – not a quick patch. Groundhogs designs and installs complete septic systems for rural homes,
              camps and cottages across Northwestern Ontario, and handles full system replacements when existing systems
              reach the end of their life.
            </p>
            <p className="text-lg" style={{ color: "var(--text-light)" }}>
              We don’t do septic pumping or cleaning. Our crews focus entirely on septic design, installation, repair
              and replacement. That means when you need a new system – or a failed one replaced – you’re working with a
              team whose whole business is designing and installing systems that last decades in Northern Ontario
              conditions.
            </p>
          </section>

          {/* Trust cards */}
          <section className="grid gap-6 md:grid-cols-3 mb-12">
            <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                1,200+ Northern Ontario Installs
              </h2>
              <p className="text-sm" style={{ color: "var(--text-light)" }}>
                Systems engineered for clay, bedrock and high water table conditions throughout Thunder Bay and the
                surrounding districts.
              </p>
            </div>
            <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                Full System Diagnosis &amp; Design
              </h2>
              <p className="text-sm" style={{ color: "var(--text-light)" }}>
                From inspection and perc testing to engineered designs and permits, we manage the full installation or
                replacement process.
              </p>
            </div>
            <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                No Pumping — Just Installation &amp; Replacement
              </h2>
              <p className="text-sm" style={{ color: "var(--text-light)" }}>
                Our crews focus entirely on system design, installation, repair and replacement — not tank cleaning.
              </p>
            </div>
          </section>

          {/* What we actually do */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              What We Actually Do (No Pumping)
            </h2>
            <p className="mb-3" style={{ color: "var(--text-light)" }}>
              Groundhogs Septic Systems focuses on engineered septic systems – not tank pumping. If you need pumping or
              cleaning, you&apos;ll want a local vacuum truck provider. When you need a new system designed, installed or
              replaced, that&apos;s where we come in.
            </p>
            <div className="grid gap-4 md:grid-cols-2" style={{ color: "var(--text-light)" }}>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Septic system design &amp; engineering</strong> for new homes, cottages and replacement systems
                  in Thunder Bay and across Northwestern Ontario.
                </li>
                <li>
                  <strong>New septic system installation</strong> for rural builds that aren&apos;t on municipal sewer.
                </li>
                <li>
                  <strong>Full septic system replacement</strong> when both tank and field are at the end of their life.
                </li>
                <li>
                  <strong>Septic field repair and upgrades</strong> where a full system replacement isn&apos;t yet required.
                </li>
              </ul>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Advanced and engineered systems</strong> including Eljen GSF, raised/mound systems and
                  solutions for small or difficult lots.
                </li>
                <li>
                  <strong>Troubleshooting &amp; diagnostics</strong> for existing
                  systems showing warning signs.
                </li>
                <li>
                  <strong>Holding tank installations</strong> for properties where a full drain field isn&apos;t feasible.
                </li>
                <li>
                  <strong>Permit coordination</strong> with Thunder Bay District Health Unit and Northwestern Health
                  Unit so your system is fully code-compliant.
                </li>
              </ul>
            </div>
          </section>

          {/* When replacement is needed */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Signs Your Septic System May Need Replacement
            </h2>
            <ul className="list-disc pl-6 space-y-2" style={{ color: "var(--text-light)" }}>
              <li>Recurring sewage backups inside the home after multiple pump-outs.</li>
              <li>Persistent soaking or ponding over the leach field or tank area.</li>
              <li>Strong sewage odours in the yard, especially after rain or melt.</li>
              <li>Known undersized or non-compliant system under the current Ontario Building Code (OBC).</li>
              <li>System older than 25–35 years, particularly with heavy clay or high groundwater.</li>
              <li>Failed inspection when buying or selling a rural property.</li>
            </ul>
          </section>

          {/* Process */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              How Our Septic Installation &amp; Replacement Process Works
            </h2>
            <ol className="list-decimal pl-6 space-y-2" style={{ color: "var(--text-light)" }}>
              <li>
                <strong>On-site assessment &amp; troubleshooting.</strong> We evaluate your existing tank, field and
                plumbing, and look at soil, slopes and groundwater.
              </li>
              <li>
                <strong>Soil and perc testing (where required).</strong> For many rural properties we will perform a
                percolation test or dig test pits to understand your options.
              </li>
              <li>
                <strong>Design &amp; permitting.</strong> We work with designers and engineers to create an OBC-compliant
                system that fits your lot, then handle permit submissions.
              </li>
              <li>
                <strong>Removal of the failed system.</strong> Old tanks, lines and distribution components are safely
                decommissioned or removed as required.
              </li>
              <li>
                <strong>Installation of the new system.</strong> Our crews complete excavation, tank placement, pipe
                runs, leach field or Eljen bed, and final grading.
              </li>
              <li>
                <strong>Final walkthrough.</strong> We review the system with you, answer maintenance questions and
                provide documentation for your records or real estate transaction.
              </li>
            </ol>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Common Questions About Septic Installation &amp; Replacement
            </h2>
            <div className="space-y-4" style={{ color: "var(--text-light)" }}>
              <div>
                <h3 className="font-semibold mb-1">How much does it cost to replace a septic system in Ontario?</h3>
                <p>
                  Most full septic system replacements (tank and field) in Ontario fall somewhere between
                  <strong> $20,000 and $40,000+</strong>, depending on soil, system type and access. Clay soil, high water
                  tables, raised beds and Eljen systems are typically at the higher end. We&apos;ll quote your property
                  specifically after reviewing soil, house size and site constraints.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Do you offer septic tank pumping or cleaning?</h3>
                <p>
                  No. We&apos;re a design, installation, repair and replacement company only. If you need pumping, you&apos;ll
                  want to call a local septic pumping or vacuum truck company in the Thunder Bay area. When you&apos;re
                  ready for design, installation or a full system replacement, that&apos;s when to call Groundhogs.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Can you install or replace septic systems in winter?</h3>
                <p>
                  Yes, winter installations are sometimes possible in Thunder Bay and surrounding areas, but they
                  require more planning and the right conditions. Frozen ground, access and snow load can affect cost
                  and timing. Whenever possible, we try to schedule installations and replacements for spring through
                  fall, but we&apos;ll review your situation and recommend what makes the most sense.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">How long will my new septic system last?</h3>
                <p>
                  A properly designed and installed septic system in Northern Ontario typically lasts <strong>20–30
                  years or more</strong>, depending on soil, usage and maintenance. System life is shortest where fields
                  are undersized, installed in heavy clay, or never pumped; it&apos;s longest when installations are based on
                  real soil data and sized conservatively – which is exactly how we design systems.
                </p>
              </div>
            </div>
          </section>

          {/* Replacement options */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Replacement Options We Install
            </h2>
            <div className="space-y-3" style={{ color: "var(--text-light)" }}>
              <p>
                Every property is different. Based on your soils, slope, water table and available area we may recommend
                one or a combination of the following replacement options:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>New conventional septic tank and leach field system.</li>
                <li>Raised bed or mound systems for clay, rock and high groundwater conditions.</li>
                <li>Eljen GSF advanced treatment systems for tight lots or sensitive sites.</li>
                <li>Tank replacement only when the field is performing but the tank is structurally compromised.</li>
                <li>Holding tanks for small or seasonal properties where a full system is not feasible.</li>
              </ul>
              <p className="mt-3">
                If you are specifically looking for a <strong>plastic or poly septic tank</strong> instead of concrete, we are an
                <strong> authorized Roth North America MultiTank dealer and installer</strong>. Our team can supply Roth MultiTank units
                or provide full supply-and-install service. Learn more on our dedicated
                <Link to="/services/roth-multitank-supplier-thunder-bay" className="text-orange-600 hover:underline">
                  {" "}
                  Roth MultiTank septic tank &amp; water tank page
                </Link>
                .
              </p>
            </div>
          </section>

          {/* Service area */}
          <section className="mb-12">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Service Area — Thunder Bay, Nipigon, Atikokan, Dryden, Greenstone &amp; More
            </h2>
            <p className="mb-3" style={{ color: "var(--text-light)" }}>
              We design and install septic systems across Thunder Bay and Northwestern Ontario, including key service
              areas such as <strong>Thunder Bay, Nipigon, Atikokan, Dryden and Greenstone</strong>, along with
              surrounding communities like Shuniah, Oliver Paipoonge, Neebing, Conmee, O’Connor, Kaministiquia, Red Rock
              and rural lakefront townships.
            </p>
            <p style={{ color: "var(--text-light)" }}>
              Visit our <Link to="/service-areas" className="text-orange-600 hover:underline">service areas</Link> page to confirm coverage in your community or cottage area.
            </p>
          </section>

          {/* CTA */}
          <section className="mt-10">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Get a Septic System Installation or Replacement Quote in Thunder Bay
            </h2>
            <p className="mb-4" style={{ color: "var(--text-light)" }}>
              If you&apos;re planning a new build, adding a cottage, or dealing with a failing septic system, the first step
              is a proper assessment. Groundhogs will inspect your site, review your options, and provide a clear
              installation or replacement plan with transparent pricing. For a deeper look at typical project ranges,
              you can also read our
              <Link to="/blog/septic-system-replacement-cost-ontario-2025" className="text-orange-600 hover:underline">
                {" "}
                septic system replacement cost guide for Ontario
              </Link>
              .
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-block px-8 py-4 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                Request Septic Quote
              </Link>
              <Link
                to="/septic-calculator"
                className="inline-block px-8 py-4 rounded-md font-semibold border-2 hover:bg-gray-50"
                style={{ borderColor: "var(--primary-orange)", color: "var(--dark-navy)" }}
              >
                Explore Septic System Costs
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SepticSystemReplacementThunderBayPage;
